// Mode FORMULAIRE — questionnaire multi-étapes + écran de résultat riche.
// Inclut : consent RGPD/nLPD, persistance localStorage, envoi anonyme au
// backend pour analytics (uniquement si consent), reset du profil.

import {
  applyAnswer,
  createInitialState,
  skipQuestion,
  withDistribution,
  DEFAULT_CONFIG,
  type EngineConfig,
} from './engine/morphology';
import { QUESTIONS } from './engine/questions';
import { classifyFromMeasurements, type Measurements } from './engine/measurements';
import { MORPHO_NAMES, MORPHO_DESCS, MORPHO_TIPS } from './engine/morphologyContent';
import { recommend, type CategorizedRecommendations, type RecommendationPrefs } from './engine/recommendation';
import { JOLIE_FILLE, type Product, type MorphoCode } from './catalog/products';
import {
  type ConditionContext,
  type EngineState,
  type MeasurementField,
  type Morphology,
  type Question,
} from './engine/types';
import { silhouetteSVG } from './ui/silhouettes';
import { FORM_STYLES } from './ui/formStyles';
import {
  clearSaved,
  getConsent,
  loadAnswers,
  loadResult,
  saveAnswers,
  saveResult,
  setConsent,
} from './prefs';
import { getOrCreateSession } from './session';

export interface EtekaFormOptions {
  catalog?: Product[];
  engine?: Partial<EngineConfig>;
  /** Backend Modal (analytics anonyme + profil member-linked). */
  apiUrl?: string;
  /** ID membre Wix si la cliente est connectée — lie le profil au compte. */
  wixMemberId?: string;
  /** Pré-remplir depuis le profil sauvegardé (si présent). */
  autoResume?: boolean;
  /**
   * Mode d'intégration :
   *   - 'home' (défaut) : Q1 "type de maillot" + recommandations produits complètes
   *   - 'product' : pas de Q1, pas de cartes produits — juste morpho + taille pour le produit affiché
   */
  mode?: 'home' | 'product';
  /** En mode 'product', l'ID du produit courant pour la reco ciblée. */
  currentProductId?: string;
  onComplete?: (state: EngineState) => void;
  onRecommend?: (reco: CategorizedRecommendations, state: EngineState) => void;
}

const TITLE_FALLBACK: Record<string, string> = {
  measurement: 'Tes mesures',
  confirmation: 'Ta silhouette',
  sizing: 'Ta taille',
  preference: 'Tes préférences',
  morphology: 'Ta silhouette',
};

const MORPHOS: MorphoCode[] = ['X', '8', 'A', 'V', 'H', 'O'];

export class EtekaForm {
  private readonly shadow: ShadowRoot;
  private readonly opts: EtekaFormOptions;
  private readonly cfg: EngineConfig;
  private readonly ids: ReturnType<typeof getOrCreateSession>;

  private state: EngineState = createInitialState();
  private answers: Record<string, string> = {};
  private measurements: Partial<Measurements> = {};
  private answeredOrder: string[] = [];
  private consentGiven = false;

  private elRoot!: HTMLElement;
  private phase: 'questions' | 'consent' | 'result' = 'questions';

  constructor(host: HTMLElement, opts: EtekaFormOptions = {}) {
    this.opts = opts;
    this.cfg = { ...DEFAULT_CONFIG, ...(opts.engine ?? {}) };
    this.ids = getOrCreateSession();
    this.shadow = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = FORM_STYLES;
    this.shadow.appendChild(style);
    this.elRoot = document.createElement('div');
    this.elRoot.className = 'ef-root';
    this.shadow.appendChild(this.elRoot);

    this.consentGiven = getConsent();
    if (opts.autoResume) this.tryResume();
    this.renderStep();
  }

  private tryResume(): void {
    const saved = loadAnswers();
    if (!saved) return;
    // Re-rejoue les réponses pour reconstruire l'état.
    for (const [qid, oid] of Object.entries(saved.answers)) {
      const q = QUESTIONS.find((x) => x.id === qid);
      if (!q) continue;
      this.answers[qid] = oid;
      this.answeredOrder.push(qid);
      if (q.kind === 'confirmation' || q.kind === 'morphology') {
        this.state = applyAnswer(this.state, q, oid, this.cfg);
      } else {
        this.state = skipQuestion(this.state, q, this.cfg);
      }
    }
    // Restaure aussi les mesures ET marque les questions de type measurement
    // comme répondues (sinon le form re-demande Q2 alors qu'on a déjà la taille).
    for (const [f, v] of Object.entries(saved.measurements)) {
      this.measurements[f as MeasurementField] = v;
      const q = QUESTIONS.find((x) => x.kind === 'measurement' && x.measurement?.field === f);
      if (q && !this.answeredOrder.includes(q.id)) {
        this.answeredOrder.push(q.id);
        this.state = skipQuestion(this.state, q, this.cfg);
      }
    }
    const m = this.measurements;
    if (typeof m.bust === 'number' && typeof m.waist === 'number' && typeof m.hips === 'number') {
      const dist = classifyFromMeasurements({
        bust: m.bust, waist: m.waist, hips: m.hips, shoulders: m.shoulders,
      });
      this.state = withDistribution(this.state, dist, this.cfg);
    }
  }

  private ctx(): ConditionContext {
    return {
      answers: this.answers,
      topMorphology: this.state.top.morphology,
      confidence: this.state.top.confidence,
      probabilities: this.state.probabilities,
      mode: this.opts.mode ?? 'home',
      currentProductId: this.opts.currentProductId,
    };
  }

  private nextQuestion(): Question | null {
    const ctx = this.ctx();
    for (const q of QUESTIONS) {
      if (this.answeredOrder.includes(q.id)) continue;
      if (q.showIf && !q.showIf(ctx)) continue;
      return q;
    }
    return null;
  }

  private remainingCount(): number {
    const ctx = this.ctx();
    let n = 0;
    for (const q of QUESTIONS) {
      if (this.answeredOrder.includes(q.id)) continue;
      if (q.showIf && !q.showIf(ctx)) continue;
      n++;
    }
    return n;
  }

  private renderStep(): void {
    if (this.phase === 'consent') return this.renderConsent();
    if (this.phase === 'result') return this.renderResult();

    const q = this.nextQuestion();
    if (!q) {
      this.phase = 'consent';
      this.renderConsent();
      return;
    }

    const stepNo = this.answeredOrder.length + 1;
    const total = this.answeredOrder.length + this.remainingCount() + 1; // +1 = étape consent
    const title = (q.title ?? TITLE_FALLBACK[q.kind] ?? '').toUpperCase();
    const segs = Array.from({ length: total }, (_, i) =>
      `<span class="ef-seg ${i < stepNo ? 'on' : ''}"></span>`,
    ).join('');

    this.elRoot.innerHTML = `
      <div class="ef-step">
        <div class="ef-progress">${segs}</div>
        <div class="ef-kicker">Question ${stepNo} sur ${total}</div>
        <h2 class="ef-title">${escapeHTML(title)}</h2>
        <p class="ef-prompt">${escapeHTML(q.prompt)}</p>
        <div class="ef-body"></div>
      </div>
    `;
    const body = this.elRoot.querySelector('.ef-body')!;
    if (q.kind === 'measurement' && q.measurement) this.renderMeasure(q, body);
    else this.renderCards(q, body);
  }

  private renderMeasure(q: Question, body: Element): void {
    const spec = q.measurement!;
    body.innerHTML = `
      <div class="ef-measure">
        <input type="number" inputmode="numeric" min="${spec.min}" max="${spec.max}"
               placeholder="${spec.placeholder ?? ''}" autocomplete="off" />
        <span class="ef-measure-unit">${spec.unit}</span>
      </div>
      <button class="ef-continue">Continuer</button>
    `;
    const input = body.querySelector<HTMLInputElement>('input')!;
    const btn = body.querySelector<HTMLButtonElement>('.ef-continue')!;
    input.focus();
    const submit = () => {
      const value = parseFloat(input.value.replace(',', '.'));
      if (Number.isNaN(value) || value < spec.min || value > spec.max) {
        input.classList.add('ef-input-error');
        return;
      }
      this.answerMeasure(q, spec.field, value);
    };
    btn.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
    input.addEventListener('input', () => input.classList.remove('ef-input-error'));
  }

  private renderCards(q: Question, body: Element): void {
    const cards = document.createElement('div');
    cards.className = 'ef-cards';
    body.appendChild(cards);
    for (const opt of q.options) {
      const card = document.createElement('button');
      card.className = 'ef-card';
      const visual = opt.image && isMorpho(opt.image)
        ? `<div class="ef-card-illus">${silhouetteSVG(opt.image as Morphology, 'light')}</div>`
        : '';
      card.innerHTML = `
        ${visual}
        <div class="ef-card-text">
          <div class="ef-card-title">${escapeHTML(opt.buttonLabel ?? opt.label)}</div>
          ${opt.description ? `<div class="ef-card-desc">${escapeHTML(opt.description)}</div>` : ''}
        </div>
      `;
      card.addEventListener('click', () => this.answerCard(q, opt.id));
      cards.appendChild(card);
    }
  }

  private answerMeasure(q: Question, field: MeasurementField, value: number): void {
    this.measurements[field] = value;
    this.answeredOrder.push(q.id);
    this.state = skipQuestion(this.state, q, this.cfg);
    const m = this.measurements;
    if (typeof m.bust === 'number' && typeof m.waist === 'number' && typeof m.hips === 'number') {
      const dist = classifyFromMeasurements({ bust: m.bust, waist: m.waist, hips: m.hips, shoulders: m.shoulders });
      this.state = withDistribution(this.state, dist, this.cfg);
    }
    this.persistProgress();
    this.renderStep();
  }

  private answerCard(q: Question, optionId: string): void {
    this.answers[q.id] = optionId;
    this.answeredOrder.push(q.id);
    if (q.kind === 'confirmation' || q.kind === 'morphology') {
      this.state = applyAnswer(this.state, q, optionId, this.cfg);
    }
    this.persistProgress();
    this.renderStep();
  }

  private persistProgress(): void {
    saveAnswers(this.answers, this.measurements as Record<string, number>);
  }

  // -- Consentement -------------------------------------------------------

  private renderConsent(): void {
    this.elRoot.innerHTML = `
      <div class="ef-step ef-consent-step">
        <div class="ef-kicker">Avant-dernière étape</div>
        <h2 class="ef-title">UTILISATION DE TES DONNÉES</h2>
        <p class="ef-prompt">Pour générer ta recommandation morphologique et ta taille personnalisée, j'utilise les réponses que tu viens de me donner.</p>
        <div class="ef-consent" id="ef-c1">
          <div class="ef-consent-box"></div>
          <div class="ef-consent-text">
            <strong>J'accepte que mes réponses servent à générer ma recommandation</strong>
            <p>Mesures, taille, morphologie, préférences. Données conservées de manière anonyme pour améliorer le service. Conforme RGPD / nLPD suisse. Tu peux supprimer ton profil à tout moment.</p>
          </div>
        </div>
        <button class="ef-continue" id="ef-c-go" disabled>Voir ma recommandation ✨</button>
      </div>
    `;
    let on = false;
    const box = this.elRoot.querySelector<HTMLElement>('#ef-c1')!;
    const go = this.elRoot.querySelector<HTMLButtonElement>('#ef-c-go')!;
    box.addEventListener('click', () => {
      on = !on;
      box.classList.toggle('checked', on);
      go.disabled = !on;
    });
    go.addEventListener('click', () => {
      this.consentGiven = true;
      setConsent(true);
      this.phase = 'result';
      this.renderResult();
      // L'upload de profil est fait depuis renderResult avec les recos.
    });
  }

  private async uploadProfile(reco?: {
    tops: string[];
    bottoms: string[];
    onepiece: string[];
    sizes?: Record<string, string>;
  }): Promise<void> {
    if (!this.opts.apiUrl || !this.consentGiven) return;
    const payload = {
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      wixMemberId: this.opts.wixMemberId ?? null,
      tool: 'form',
      answers: this.answers,
      measurements: this.measurements,
      morphology: this.state.top.morphology,
      confidence: this.state.top.confidence,
      probabilities: this.state.probabilities,
      recommendedProducts: reco ?? {},
      timestamp: Date.now(),
    };
    try {
      await fetch(`${this.opts.apiUrl}/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch { /* silencieux */ }
  }

  /** Loggue un clic sur "Voir →" — utilisé par le dashboard pour le taux de CTA. */
  private trackCtaClick(productId: string): void {
    if (!this.opts.apiUrl || !this.consentGiven) return;
    try {
      void fetch(`${this.opts.apiUrl}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: this.ids.userId,
          wixMemberId: this.opts.wixMemberId ?? null,
          type: 'cta_click',
          productId,
          morphology: this.state.top.morphology,
        }),
        keepalive: true,
      });
    } catch { /* silencieux */ }
  }

  // -- Résultat riche -----------------------------------------------------

  private renderResult(): void {
    const top = this.state.top.morphology;
    const conf = Math.round(this.state.top.confidence * 100);
    const prefs = this.buildPrefs();
    const catalog = this.opts.catalog ?? JOLIE_FILLE;
    const reco = recommend(top as MorphoCode, prefs, catalog);
    const isProductMode = this.opts.mode === 'product';

    saveResult(top, this.state.top.confidence);
    this.opts.onComplete?.(this.state);
    this.opts.onRecommend?.(reco, this.state);

    // Mode PRODUCT : on cherche le produit courant et on n'affiche QUE
    // sa taille recommandée (pas de comparaison avec les autres modèles).
    let currentProductReco: typeof reco.tops[number] | null = null;
    if (isProductMode && this.opts.currentProductId) {
      const all = [...reco.tops, ...reco.bottoms, ...reco.onepiece];
      currentProductReco = all.find((s) => s.product.id === this.opts.currentProductId) ?? null;
    }

    const probsRow = MORPHOS.map((m) => {
      const pct = Math.round((this.state.probabilities[m] ?? 0) * 100);
      return `<div class="ef-score-row">
        <span class="ef-s-letter">${m}</span>
        <div class="ef-s-track"><div class="ef-s-fill" style="width:${pct}%"></div></div>
        <span class="ef-s-pct">${pct}%</span>
      </div>`;
    }).join('');

    const tips = MORPHO_TIPS[top].map((t) => `<li>${escapeHTML(t)}</li>`).join('');

    const profile = [
      ['Morphologie', `${top} · ${MORPHO_NAMES[top]}`],
      ['Taille haut', prefs.topSize ?? '—'],
      ['Taille bas', prefs.bottomSize ?? '—'],
      ['Couvrance', prefs.coverage ?? '—'],
      ['Maintien', prefs.support ?? '—'],
      ['Fit', prefs.fit ?? '—'],
    ].map(([l, v]) => `<div class="ef-prof"><div class="ef-prof-l">${escapeHTML(String(l))}</div><div class="ef-prof-v">${escapeHTML(String(v))}</div></div>`).join('');

    this.elRoot.innerHTML = `
      <div class="ef-result">
        <div class="ef-result-head">
          <div class="ef-result-illus">${silhouetteSVG(top, 'light')}</div>
          <div>
            <div class="ef-kicker">Ta morphologie · ${conf}%</div>
            <h2 class="ef-title">${escapeHTML(MORPHO_NAMES[top])}</h2>
            <p class="ef-prompt">${escapeHTML(MORPHO_DESCS[top])}</p>
          </div>
        </div>

        <div class="ef-profile-grid">${profile}</div>

        <div class="ef-block">
          <div class="ef-block-title">Correspondance morphologique</div>
          <div class="ef-scores">${probsRow}</div>
        </div>

        <div class="ef-block ef-tips-block">
          <div class="ef-block-title">Conseils pour ta silhouette</div>
          <ul class="ef-tips">${tips}</ul>
        </div>

        ${isProductMode
          ? `
        <div class="ef-reco-section">
          <h3 class="ef-reco-title">Ta taille pour ce maillot</h3>
          ${currentProductReco
            ? `
            <div class="ef-block">
              <div class="ef-block-title">${escapeHTML(currentProductReco.product.name)}</div>
              ${this.sizeGaugeHTML(currentProductReco, true)}
              ${currentProductReco.cupNote ? `<p class="stat-sub" style="color: var(--ef-rust); font-weight: 600; margin-top: 8px;">💡 ${escapeHTML(currentProductReco.cupNote)}</p>` : ''}
              <p class="ef-prompt" style="margin-top: 16px;">${escapeHTML(currentProductReco.reason)}</p>
            </div>
            `
            : `<p class="ef-prompt">On a déterminé ta morphologie : <strong>${top}</strong>. Reviens en page d'accueil pour voir tous les modèles qui te conviennent.</p>`}
        </div>
        `
          : `
        <div class="ef-reco-section">
          <h3 class="ef-reco-title">Nos recommandations</h3>
          <p class="ef-reco-sub">Morphologie ${top} · Couvrance ${escapeHTML(prefs.coverage ?? '—')}</p>
          ${reco.showTops ? `<p class="ef-reco-cat">Hauts</p><div class="ef-reco-grid" id="ef-r-tops"></div>` : ''}
          ${reco.showBottoms ? `<p class="ef-reco-cat">Bas</p><div class="ef-reco-grid" id="ef-r-bots"></div>` : ''}
          ${reco.showOnepiece ? `<p class="ef-reco-cat">Maillot une pièce</p><div class="ef-reco-grid" id="ef-r-1p"></div>` : ''}
        </div>
        `}

        <div class="ef-actions">
          <button class="ef-continue" id="ef-restart">Refaire le quiz</button>
          <button class="ef-restart" id="ef-clear">Supprimer mon profil</button>
        </div>
      </div>
    `;

    const fill = (id: string, items: typeof reco.tops) => {
      const el = this.elRoot.querySelector(id);
      if (!el) return;
      el.innerHTML = items.map((s) => this.productCard(s)).join('');
    };
    // Mode home : afficher les cartes produits par catégorie
    if (!isProductMode) {
      if (reco.showTops) fill('#ef-r-tops', reco.tops);
      if (reco.showBottoms) fill('#ef-r-bots', reco.bottoms);
      if (reco.showOnepiece) fill('#ef-r-1p', reco.onepiece);
      // Charge les vraies photos Wix de manière asynchrone
      this.hydrateProductImages();
    }

    // Tracking CTA : intercepte les clics sur "Voir →" pour mesurer le
    // taux de clic post-quiz (utilisé par le dashboard).
    this.elRoot.querySelectorAll<HTMLAnchorElement>('.ef-product-link').forEach((link) => {
      link.addEventListener('click', () => {
        const pid = link.dataset.productId ?? '';
        if (pid) this.trackCtaClick(pid);
      });
    });

    this.elRoot.querySelector('#ef-restart')!.addEventListener('click', () => this.restart());
    this.elRoot.querySelector('#ef-clear')!.addEventListener('click', () => this.clearProfile());

    // Envoi du profil avec les recos finales pour les analytics.
    // `sizes` : taille recommandée par produit — comparée ensuite à la taille
    // achetée (Wix) et à la taille habituelle (Q4/Q5) dans le dashboard.
    void this.uploadProfile({
      tops: reco.tops.map((s) => s.product.id),
      bottoms: reco.bottoms.map((s) => s.product.id),
      onepiece: reco.showOnepiece ? reco.onepiece.map((s) => s.product.id) : [],
      sizes: Object.fromEntries(
        [...reco.tops, ...reco.bottoms, ...(reco.showOnepiece ? reco.onepiece : [])]
          .filter((s) => s.recommendedSize && s.recommendedSize !== '—')
          .map((s) => [s.product.id, s.recommendedSize]),
      ),
    });
  }

  /** Génère la jauge HTML d'une taille (échelle + tick mis en valeur). */
  private sizeGaugeHTML(s: CategorizedRecommendations['tops'][number], large = false): string {
    const g = s.sizeGauge;
    if (!g || !g.scale.length) return '';
    const ticks = g.scale.map((sz) => {
      let cls = 'ef-size-tick';
      if (sz === g.primary) cls += ' primary';
      else if (sz === g.alternative) cls += ' alt';
      return `<span class="${cls}">${escapeHTML(sz)}</span>`;
    }).join('');
    return `
      <div class="ef-size-gauge${large ? ' large' : ''}">
        <div class="ef-size-track">${ticks}</div>
        ${g.hint ? `<div class="ef-size-hint">${escapeHTML(g.hint)}</div>` : ''}
      </div>
    `;
  }

  /** Charge la vraie photo Wix de manière asynchrone et remplace le placeholder. */
  private hydrateProductImages(): void {
    if (!this.opts.apiUrl) return;
    const root = this.shadow.querySelectorAll<HTMLElement>('.ef-reco-img[data-product-url]');
    root.forEach((el) => {
      const url = el.getAttribute('data-product-url');
      if (!url || el.dataset.hydrated === '1') return;
      el.dataset.hydrated = '1';
      // Cache localStorage 1 jour pour éviter de re-fetcher à chaque vue
      const ckey = 'eteka.img.' + url;
      try {
        const raw = localStorage.getItem(ckey);
        if (raw) {
          const obj = JSON.parse(raw) as { img: string; exp: number };
          if (obj.exp > Date.now()) {
            el.style.background = `url(${obj.img}) center/cover no-repeat`;
            el.classList.add('ef-reco-img-real');
            return;
          }
        }
      } catch { /* ignore */ }
      fetch(`${this.opts.apiUrl}/wix-image?productUrl=${encodeURIComponent(url)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data: { image?: string } | null) => {
          if (data?.image) {
            el.style.background = `url(${data.image}) center/cover no-repeat`;
            el.classList.add('ef-reco-img-real');
            try {
              localStorage.setItem(ckey, JSON.stringify({ img: data.image, exp: Date.now() + 86400000 }));
            } catch { /* ignore */ }
          }
        })
        .catch(() => { /* silencieux : gardons le gradient placeholder */ });
    });
  }

  private productCard(s: CategorizedRecommendations['tops'][number]): string {
    const p = s.product;
    const colors = (p.colors ?? []).map(
      (c) => `<div class="ef-swatch" style="background:${c.bg}" title="${escapeAttr(c.name)}"></div>`,
    ).join('');
    const link = p.productUrl ?? '#';
    const target = p.productUrl ? 'target="_blank" rel="noopener"' : '';
    const imgUrlAttr = p.productUrl ? `data-product-url="${escapeAttr(p.productUrl)}"` : '';
    // Si la photo est connue (catalog statique), on la pose tout de suite.
    // Sinon on garde le gradient + hydrate async via /wix-image.
    const imgStyle = p.image
      ? `background:url(${escapeAttr(p.image)}) center/cover no-repeat`
      : `background:linear-gradient(135deg, ${p.color}, ${p.color}aa)`;
    const imgClass = p.image ? 'ef-reco-img ef-reco-img-real' : 'ef-reco-img';
    return `<a class="ef-reco-card ef-product-link" href="${escapeAttr(link)}" ${target} data-product-id="${escapeAttr(p.id)}">
      <div class="${imgClass}" ${imgUrlAttr} style="${imgStyle}">
        <div class="ef-reco-img-name">${escapeHTML(p.name)}<br><span>${escapeHTML(p.type)}</span></div>
        <span class="ef-match ef-match-${s.badge.level}">${escapeHTML(s.badge.label)}</span>
      </div>
      <div class="ef-reco-body">
        <div class="ef-reco-ref">${escapeHTML(p.id)}</div>
        <div class="ef-reco-name">${escapeHTML(p.name)}</div>
        <div class="ef-reco-type">${escapeHTML(p.type)}</div>
        ${this.sizeGaugeHTML(s)}
        <div class="ef-reco-why">${escapeHTML(s.reason)}</div>
        <div class="ef-color-row">${colors}</div>
        <div class="ef-reco-cta">Voir →</div>
      </div>
    </a>`;
  }

  private buildPrefs(): RecommendationPrefs {
    const a = this.answers;
    const prefs: RecommendationPrefs = { mode: this.opts.mode ?? 'home' };

    // Q1 — Type de maillot (mode home uniquement)
    const q1 = a['q1_type'];
    if (q1 === 'two_pieces' || q1 === 'one_piece' || q1 === 'both') prefs.q1Type = q1;

    // Q3 — Bande + bonnet
    if (a['q3_band']) prefs.band = a['q3_band'];
    if (a['q3_cup']) {
      prefs.cup = a['q3_cup'];
      const cup = a['q3_cup'];
      if (cup === 'A' || cup === 'B') prefs.smallCup = true;
      if (cup === 'DD_E' || cup === 'F' || cup === 'G_plus') prefs.bigCup = true;
    }

    // Q4/Q5 — Tailles habituelles
    if (a['q4_top']) prefs.topSize = a['q4_top'];
    if (a['q5_bot']) prefs.bottomSize = a['q5_bot'];

    // Q10 — Couvrance bas
    const cov = a['q10_coverage'];
    if (cov === 'minimal' || cov === 'cheeky' || cov === 'classique' || cov === 'couvrant') prefs.coverage = cov;

    // Q11 — Question adaptative (5 cas) → en déduire maintien + fit
    const q11_big = a['q11_big_cup'];
    if (q11_big === 'maintien') prefs.support = 'strong';
    else if (q11_big === 'valeur') prefs.support = 'medium';
    else if (q11_big === 'equilibre') prefs.support = 'medium';

    const q11_small = a['q11_small_cup'];
    if (q11_small === 'volume') prefs.support = 'medium';
    else if (q11_small === 'liberte') prefs.support = 'light';
    else if (q11_small === 'equilibre') prefs.support = 'medium';

    const q11_diff = a['q11_diff_size'];
    if (q11_diff === 'tight') prefs.fit = 'tight';
    else if (q11_diff === 'loose') prefs.fit = 'loose';
    else if (q11_diff === 'depends') prefs.fit = 'depends';

    const q11_def = a['q11_default'];
    if (q11_def === 'leger') prefs.support = 'light';
    else if (q11_def === 'modere') prefs.support = 'medium';
    else if (q11_def === 'fort') prefs.support = 'strong';

    return prefs;
  }

  private restart(): void {
    this.state = createInitialState();
    this.answers = {};
    this.measurements = {};
    this.answeredOrder = [];
    this.phase = 'questions';
    this.renderStep();
  }

  private clearProfile(): void {
    // 1. Purge le localStorage + sessionStorage côté navigateur
    clearSaved();
    setConsent(false);
    this.consentGiven = false;
    // 2. Demande au backend de supprimer le profil lié (member ou anonyme)
    if (this.opts.apiUrl) {
      const body = JSON.stringify({
        userId: this.ids.userId,
        sessionId: this.ids.sessionId,
        wixMemberId: this.opts.wixMemberId ?? null,
      });
      try {
        fetch(`${this.opts.apiUrl}/profile`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => { /* silencieux */ });
      } catch { /* silencieux */ }
    }
    // 3. Reset l'état interne du form
    this.restart();
  }
}

function isMorpho(s: string): boolean {
  return ['A', 'V', 'H', 'X', '8', 'O', 'unknown'].includes(s);
}
function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
function escapeAttr(s: string): string { return escapeHTML(s); }
